import { z } from 'zod'

// Background settings schema
export const BackgroundSettingsSchema = z.object({
  showBackgroundImage: z.boolean(),
  gaugeBackgroundImage: z.string(),
  gaugeBackgroundOffsetX: z.number(),
  gaugeBackgroundOffsetY: z.number(),
})

// Needle settings schema
export const NeedleSettingsSchema = z.object({
  showNeedle: z.boolean(),
  needleLength: z.number(),
  needleWidth: z.number(),
  needleColor: z.string(),
  needlePointEnabled: z.boolean(),
  needlePointLength: z.number(),
  needleShadowEnabled: z.boolean(),
  needleShadowColor: z.string(),
  needleShadowOffsetX: z.number(),
  needleShadowOffsetY: z.number(),
  needleShadowBlur: z.number(),
  showCustomNeedle: z.boolean(),
  customNeedleImage: z.string().optional(),
  customNeedleSize: z.number(),
  customNeedleRotation: z.number(),
  customNeedleOffsetX: z.number(),
  customNeedleOffsetY: z.number(),
})

// Bezel settings schema
export const BezelSettingsSchema = z.object({
  showBezel: z.boolean(),
  bezelImage: z.string(),
  bezelOffsetX: z.number(),
  bezelOffsetY: z.number(),
})

// Label layer schema
export const LabelLayerSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('label'),
  visible: z.boolean(),
  showCurrentValue: z.boolean(),
  label: z.string(),
  labelColor: z.string(),
  labelFontSize: z.number(),
  labelOffset: z.number(),
  labelOffsetX: z.number(),
  labelOffsetY: z.number(),
  labelRotation: z.number(),
})

// Generated label settings schema (same as LabelLayerSchema)
export const GeneratedLabelSettingsSchema = LabelLayerSchema

// Generated segment settings schema
export const GeneratedSegmentSettingsSchema = z.object({
  lineThickness: z.number(),
  lineColor: z.string(),
  showLine: z.boolean(),
  showLabel: z.boolean(),
  label: z.string(),
  labelColor: z.string(),
  labelFontSize: z.number(),
  labelOffset: z.number(),
  labelOffsetX: z.number(),
  labelOffsetY: z.number(),
  labelRotation: z.number(),
  visible: z.boolean(),
  minValue: z.number(),
  maxValue: z.number(),
})

// Segment layer schema
export const BaseSegmentLayerSchema = z.object({
  type: z.literal('segment'),
  id: z.string(),
  name: z.string(),
  visible: z.boolean(),
  minValue: z.number(),
  maxValue: z.number(),
  lineColor: z.string(),
  showLine: z.boolean(),
  lineThickness: z.number(),
  showLabel: z.boolean(),
  label: z.string(),
  labelColor: z.string(),
  labelFontSize: z.number(),
  labelOffset: z.number(),
  labelOffsetX: z.number(),
  labelOffsetY: z.number(),
  labelRotation: z.number(),
})

// Segment settings schema
export const SegmentSettingsSchema = z
  .object({
    visible: z.boolean(),
    showLine: z.boolean(),
    lineThickness: z.number(),
    lineColor: z.string(),
    showLabel: z.boolean(),
    label: z.string(),
    labelColor: z.string(),
    labelFontSize: z.number(),
    labelOffset: z.number(),
    labelOffsetX: z.number(),
    labelOffsetY: z.number(),
    labelRotation: z.number(),
    minValue: z.number(),
    maxValue: z.number(),
  })
  .partial()

// Main gauge settings schema
export const GaugeSettingsSchema = z.object({
  // Basic gauge settings
  id: z.string().optional(),
  width: z.string(),
  height: z.string(),
  gaugeBackgroundColor: z.string(),
  minValue: z.number(),
  maxValue: z.number(),
  currentValue: z.number(),
  startAngle: z.number(),
  endAngle: z.number(),
  baseRadius: z.union([z.number(), z.string()]),
  segments: z.array(BaseSegmentLayerSchema),
  labels: z.array(LabelLayerSchema).default([]),
  defaultLabelSettings: LabelLayerSchema.optional(),
  defaultSegmentSettings: SegmentSettingsSchema.optional(),
  layerOrder: z.array(z.string()),
  hiddenLayers: z.array(z.string()),

  // Tick settings
  numTicks: z.number(),
  tickSize: z.number(),
  tickWidth: z.number(),
  tickColor: z.string(),
  fillerTicks: z.number(),
  fillerTickSize: z.number(),
  fillerTickWidth: z.number(),
  fillerTickColor: z.string(),
  showFillerTicks: z.boolean(),

  // Label settings
  showLabels: z.boolean().default(true),
  labelsInside: z.boolean().default(false),
  horizontalLabels: z.boolean().default(false),
  showMinLabel: z.boolean().default(true),
  showMaxLabel: z.boolean().default(true),
  labelOffset: z.number().default(10),
  labelFontSize: z.number(),
  labelFont: z.string(),
  labelColor: z.string(),

  // Line settings
  showLine: z.boolean(),
  lineThickness: z.number(),
  lineColor: z.string(),

  // Value arc settings
  showValueArc: z.boolean(),
  valueArcColor: z.string(),
  valueArcThickness: z.number(),

  // Component settings
  needleSettings: NeedleSettingsSchema,
  bezelSettings: BezelSettingsSchema,
  backgroundSettings: BackgroundSettingsSchema,
  generatedLabelSettings: GeneratedLabelSettingsSchema,
  generatedSegmentSettings: GeneratedSegmentSettingsSchema,

  // Hub Settings
  showCustomHub: z.boolean(),
  customHubImage: z.string(),
  customHubRotation: z.number(),
  customHubOffsetX: z.number(),
  customHubOffsetY: z.number(),
  customHubImageSize: z.number(),
  centerCircleSize: z.number(),
  centerCircleColor: z.string(),
  centerCircleBorderColor: z.string(),
  centerCircleBorderWidth: z.number(),
  centerCircleShadowEnabled: z.boolean(),
  centerCircleShadowColor: z.string(),
  centerCircleShadowOffsetX: z.number(),
  centerCircleShadowOffsetY: z.number(),
  centerCircleShadowBlur: z.number(),
  customHubImageRotationOffset: z.number(),
})

export type GaugeSettingsType = z.infer<typeof GaugeSettingsSchema>
